import {
  Box,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  Stack,
  Typography,
  Divider,
  SvgIcon,
} from '@mui/material'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { capitalizeFirstLetter } from '@hapana/shared/utils/string.util'

import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search-small.svg'
import TimesIcon from '@hapana/shared/web/styles/assets/svgs/times.svg'
import CheckIcon from '@hapana/shared/web/styles/assets/svgs/check.svg'
import Switch from '../switch/switch.component'
import { SWITCH_TYPES } from '../switch/switch.constants'
import TextField from '../text-field/text-field.component'
import Button from '../button/button.component'
import { BUTTON_SIZE, BUTTON_VARIANTS } from '../button/button.constants'

import { styles } from './checkbox-group.styles'
import { CHECKBOX_GROUP_SEARCH_FIELD_NAME } from './checkbox-group.constants'

const CheckboxGroup = ({
  label,
  name,
  options,
  switchOptions,
  searchPlaceholder,
  optionsStyles,
  buttonOptions,
  startAdornmentStyle,
  checkboxOptions,
  renderNoOptions,
  shouldCaptalize,
  hasDivider,
}) => {
  const { register, watch, setValue } = useFormContext()
  const buttonRef = useRef(null)
  const {
    field: { value, onChange },
  } = useController({
    name,
    register,
  })

  const [selectAll, setSelectAll] = useState(false)

  const searchField = watch(CHECKBOX_GROUP_SEARCH_FIELD_NAME)

  const [filteredOptions, setFilteredOptions] = useState(options)

  useEffect(() => {
    if (searchField) {
      const newOptions = options?.filter((option) =>
        option.label.toLowerCase().includes(searchField.toLowerCase())
      )
      setFilteredOptions(newOptions)
    } else {
      setFilteredOptions(options)
    }
  }, [searchField, options])

  const onSingleCheckboxChange = (event) => {
    const newValues = [...value]
    const itemIndex = newValues.findIndex((item) => (item.value || item.id) === event.target.name)
    if (event.target.checked) {
      const selectedOption = options.find((option) => option.value === event.target.name)
      if (selectedOption) {
        newValues.push({
          label: selectedOption.label,
          value: event.target.name,
        })
      }
    } else {
      newValues.splice(itemIndex, 1)
    }

    if (switchOptions) {
      if (newValues?.length !== options.length) {
        setValue(switchOptions.name, false)
      } else {
        setValue(switchOptions.name, true)
      }
    }

    onChange(newValues)
  }

  useEffect(() => {
    setSelectAll(checkboxOptions)
  }, [checkboxOptions])

  const onSwitchChange = (e) => {
    e.preventDefault()
    const isChecked = buttonOptions ? !selectAll : e?.target?.checked
    if (buttonOptions) {
      setSelectAll(isChecked)
    } else {
      setValue(switchOptions.name, isChecked)
    }
    if (isChecked) {
      const newValues = [...options]
      if (value?.length === options.length) {
        return
      }
      onChange(newValues)
    } else if (!isChecked) {
      if (value?.length === options.length) {
        onChange([])
      }
      return
    }
  }

  const LabelContent = ({ singleCheckboxLabel }) => (
    <>
      <Stack direction="row" spacing={1} sx={styles.labelWrapper}>
        {singleCheckboxLabel &&
          (typeof singleCheckboxLabel === 'string' ? (
            <Typography variant="inherit" component="div">
              {shouldCaptalize ? singleCheckboxLabel : capitalizeFirstLetter(singleCheckboxLabel)}
            </Typography>
          ) : (
            singleCheckboxLabel // Render JSX as-is if it's not a string
          ))}
      </Stack>
    </>
  )
  const CheckboxItem = ({ option }) => {
    const isChecked = !!value?.find((item) => (item.value || item.id) === option.value)
    return (
      <MuiCheckbox
        test-id={`checkbox-group-${option.value}`}
        color="primary"
        name={option.value}
        checked={isChecked}
        onChange={onSingleCheckboxChange}
        sx={styles.checkbox}
        required={(option && option?.required) || false}
        disabled={(option && option?.disabled) || false}
      />
    )
  }

  const RenderNoOptions = ({ renderNoOptions }) => {
    const { icon, description, subDescription, style } = renderNoOptions
    return (
      <Box sx={renderNoOptions && styles.noRenderContainer}>
        <Box sx={renderNoOptions && styles.noRenderSubContainer}>
          <SvgIcon sx={style.icon} component={icon} />
          <Typography mt={1.5} sx={style.description}>
            {description}
          </Typography>
          <Typography mt={0.5} sx={style.subDescription}>
            {subDescription}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {label && (
        <Typography typography="header4" color="gray.80">
          {label}
        </Typography>
      )}
      {!renderNoOptions && searchPlaceholder && (
        <Box mt={1.5} sx={styles.searchContainer}>
          <TextField
            name={CHECKBOX_GROUP_SEARCH_FIELD_NAME}
            test-id={`checkbox-group-${CHECKBOX_GROUP_SEARCH_FIELD_NAME}`}
            id={CHECKBOX_GROUP_SEARCH_FIELD_NAME}
            startAdornment={SearchIcon}
            startAdornmentStyle={startAdornmentStyle}
            sx={styles.searchInput}
            placeholder={searchPlaceholder}
          />
        </Box>
      )}
      {!renderNoOptions ? (
        <>
          <Box>
            {buttonOptions && (
              <Box sx={styles.buttonOptions}>
                <Typography mt={0.75} color="gray.64" typography="header3">
                  {buttonOptions?.externalLabel}
                </Typography>
                <Button
                  test-id={`checkbox-group-selectAllLabel`}
                  label={selectAll ? buttonOptions.unSelectAllLabel : buttonOptions.selectAllLabel}
                  size={BUTTON_SIZE.SMALL}
                  variant={BUTTON_VARIANTS.TEXT}
                  startIcon={selectAll ? TimesIcon : CheckIcon}
                  sx={styles.switch}
                  onClick={onSwitchChange}
                  ref={buttonRef}
                />
              </Box>
            )}
          </Box>
          <Box sx={[styles.optionsContainer, styles.scroller, optionsStyles]}>
            {filteredOptions?.map((option) => {
              const isChecked = value?.find((item) => item.value === option.value)

              return (
                <Fragment key={option.value}>
                  <FormControlLabel
                    sx={[styles.label, isChecked && { ...styles.labelSelected }]}
                    control={<CheckboxItem option={option} disabled={true} />}
                    required={option?.required || false}
                    disabled={option?.required || false}
                    label={
                      option?.htmlLabel ? (
                        <LabelContent singleCheckboxLabel={option?.htmlLabel} />
                      ) : (
                        option.label
                      )
                    }
                    labelPlacement="end"
                  />
                  {(buttonOptions || hasDivider) && (
                    <Divider sx={hasDivider ? styles.hasDividerStyles : styles.divider} />
                  )}
                </Fragment>
              )
            })}
          </Box>
          {switchOptions && (
            <Box mt={2.5}>
              <Switch
                test-id={switchOptions.name}
                label={switchOptions.label}
                name={switchOptions.name}
                type={SWITCH_TYPES.LABEL}
                sx={styles.switch}
                onChange={onSwitchChange}
              />
            </Box>
          )}
        </>
      ) : (
        <RenderNoOptions renderNoOptions={renderNoOptions} />
      )}
    </Box>
  )
}

CheckboxGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  shouldCaptalize: PropTypes.bool,
  hasDivider: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  singleCheckboxLabel: PropTypes.string,
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    disabled: PropTypes.bool,
    htmlLabel: PropTypes.string,
  }),
  switchOptions: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
  }),
  buttonOptions: PropTypes.shape({
    selectAllLabel: PropTypes.string,
    unSelectAllLabel: PropTypes.string,
    externalLabel: PropTypes.string,
  }),
  searchPlaceholder: PropTypes.string,
  optionsStyles: PropTypes.object,
  startAdornmentStyle: PropTypes.object,
  checkboxOptions: PropTypes.bool,
  renderNoOptions: PropTypes.object,
}

export default CheckboxGroup
